import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import type { Customer } from "@/api/token/customer";
import type { Product } from "@/api/token/product";
import type { ProductToken } from "@/api/token/product-token";
import { TokenApiService } from "@/api/token/token-service";
import { env } from "@/config/environment-vars";
import { AuthContext } from "./auth-context";
import { useCustomerID } from "./customer-id-repository";

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { user, logout, getAccessTokenSilently, isAuthenticated, isLoading: isAuth0Loading } = useAuth0();
  const queryClient = useQueryClient();
  const [oneTimeProductTokenSuccessCallback, setOneTimeProductTokenSuccessCallback] = useState<
    ((customer: Customer) => void) | null
  >(null);

  const tokenAPIService = new TokenApiService({
    productName: env.VITE_PROJECT_CODE,
    baseUrl: env.VITE_TOKEN_URL,
  });

  const signOut = useCallback(() => {
    queryClient.clear();
    return logout({
      logoutParams: { returnTo: window.location.origin },
    });
  }, [queryClient, logout]);

  const [authToken, setAuthToken] = useState<string | null>(null);
  const { customerID, setCustomerID } = useCustomerID();

  useEffect(() => {
    getAccessTokenSilently()
      .then((token) => {
        setAuthToken(token);
      })
      .catch((error) => {
        console.error("Failed to get access token:", error);
        setAuthToken(null);
      });
  }, [getAccessTokenSilently]);

  const getCustomersResult = useQuery<Customer[]>({
    queryKey: ["customers"],
    queryFn: () => tokenAPIService.getCustomers({ authToken: authToken as string }),
    enabled: !!authToken,
  });

  const getProductsResult = useQuery<Product[]>({
    queryKey: ["products", customerID],
    queryFn: () =>
      tokenAPIService.getProducts({
        authToken: authToken as string,
        customerId: customerID as number,
      }),
    enabled: !!authToken && !!customerID,
  });

  const getProductTokenResult = useQuery<ProductToken>({
    queryKey: ["productToken", customerID],
    queryFn: async () => {
      const result = await tokenAPIService.getProductToken({
        authToken: authToken as string,
        customerId: customerID as number,
      });

      queryClient.setQueryDefaults(["productToken", customerID], {
        staleTime: result.expiresIn * 1000,
        initialDataUpdatedAt: Date.now(),
      });

      if (oneTimeProductTokenSuccessCallback) {
        const customer = getCustomersResult.data?.find((item) => item.customerId === customerID);
        oneTimeProductTokenSuccessCallback(customer as Customer);
        setOneTimeProductTokenSuccessCallback(null);
      }

      return result;
    },
    enabled: !!authToken && !!customerID,
  });

  useEffect(() => {
    if (getProductTokenResult.error) {
      signOut();
    }
  }, [getProductTokenResult.error, signOut]);

  return (
    <AuthContext.Provider
      value={{
        user: user ? { name: user.name, email: user.email, picture: user.picture } : null,
        isAuthenticated,
        isAuthenticationLoading: isAuth0Loading,

        customerId: customerID,
        customerName: getCustomersResult.data?.find((customer) => customer.customerId === customerID)?.name,
        selectCustomer({ customerId, onCustomerSelected }) {
          setOneTimeProductTokenSuccessCallback(() => onCustomerSelected);
          setCustomerID(customerId);
          queryClient.removeQueries({
            queryKey: ["productToken", customerId],
          });
        },
        customers: getCustomersResult.data,
        isCustomersLoading: getCustomersResult.isLoading,
        customersError: getCustomersResult.error,

        isAuthorized: !!getProductTokenResult.data,
        isProductTokenLoading: getProductTokenResult.isLoading,
        productCode: env.VITE_PROJECT_CODE,
        productToken: getProductTokenResult.data?.token,
        productTokenError: getProductTokenResult.error,
        products: getProductsResult.data,
        isProductsLoading: getProductsResult.isLoading,
        productsError: getProductsResult.error,

        isLoading:
          isAuth0Loading ||
          getCustomersResult.isLoading ||
          getProductTokenResult.isLoading ||
          getProductsResult.isLoading,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
